import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import sanitizeFileName from "helpers/sanitizeFileName";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface SocialNetworksBanner {
  id: number;
  href: string;
  priority: number;
  alt: string;
  image: string;
}

export interface SocialNetwork {
  id: number;
  title: string;
  geo: string;
  isActive: boolean;
  items: SocialNetworksBanner[];
}

export type DraftItemBanner = Omit<SocialNetworksBanner, "image"> & {
  imageHref?: string;
  image?: File;
  isModified: boolean;
};

type SocialNetworkDraftItem = Omit<SocialNetwork, "items"> & {
  isModified: boolean;
  items: DraftItemBanner[];
};

export interface SocialNetworksState {
  list: Omit<SocialNetwork, "items">[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: SocialNetwork["id"][];
  processingItemId?: SocialNetwork["id"];
  processingBannerId?: SocialNetworksBanner["id"];
  activePage: number;
  totalItems: number;
  totalPages: number;
  draftItem: SocialNetworkDraftItem;
}

interface GetListPayload {
  limit?: number;
  offset?: number;
  title?: string;
  countries?: string[];
  isActive?: number;
  order?: Partial<Record<keyof Omit<SocialNetwork, "items">, string>>;
}

type CreateUpdateDraftItemPayload = Partial<
  Omit<SocialNetworkDraftItem, "items"> & {
    items: Pick<DraftItemBanner, "alt" | "href" | "priority" | "image">[];
  }
>;

interface BunchUpdatePayload {
  ids: number[];
  isActive: number;
}

const initialSocialNetwork: SocialNetworkDraftItem = {
  id: 0,
  title: "",
  geo: "",
  isActive: false,
  items: [],
  isModified: false,
};

const initialState: SocialNetworksState = {
  list: [],
  selectedItems: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  draftItem: initialSocialNetwork,
};

const createDataForUpdate = (data: CreateUpdateDraftItemPayload) => {
  const formData = new FormData();

  const { id, isModified, ...inputData } = data;

  Object.keys(inputData).forEach((key) => {
    const k = key as keyof SocialNetwork;

    switch (k) {
      case "isActive":
        formData.append("isActive", `${data[k] ? 1 : 0}`);
        return;

      case "geo":
        formData.append("country", `${data[k]}`);
        return;

      case "items": {
        const banners = data[k] as DraftItemBanner[];

        banners.forEach((banner, index) => {
          formData.append(`items[${index}][id]`, `${banner.id}`);
          formData.append(`items[${index}][alt]`, banner.alt ?? "");
          formData.append(`items[${index}][href]`, banner.href ?? "");
          formData.append(
            `items[${index}][priority]`,
            `${banner.priority ?? index + 1}`,
          );

          if (banner.image instanceof File) {
            const safeName = sanitizeFileName(banner.image.name);

            formData.append(
              `items[${index}][image]`,
              new File([banner.image], safeName, {
                type: banner.image.type,
              }),
            );
          }
        });

        return;
      }

      default:
        formData.append(k, `${data[k]}`);
    }
  });

  return formData;
};

const createDraftItem = (data: SocialNetwork) => {
  const { id, title, isActive, items, geo } = data;

  const banners: DraftItemBanner[] = items.map((item) => ({
    id: item.id,
    href: item.href,
    priority: item.priority,
    alt: item.alt,
    imageHref: item.image,
    isModified: false,
  }));

  return {
    id,
    title,
    isActive,
    geo,
    isModified: false,
    items: banners,
  };
};

const getSocialNetworksList = createAsyncThunk(
  "cms-social-networks/list",
  async (data: GetListPayload) => {
    const result = await instance.post(apiRoutes.socialNetworksList, data);
    return result.data;
  },
);

const getSocialNetworkItem = createAsyncThunk(
  "cms-social-networks/item",
  async (id: number) => {
    const result = await instance.get(`${apiRoutes.socialNetworksItem}/${id}`);
    return result.data as SocialNetwork;
  },
);

const deleteSocialNetworkItem = createAsyncThunk(
  "cms-social-networks/delete",
  async (id: number) => {
    await instance.delete(`${apiRoutes.socialNetworksDelete}/${id}`);
    return true;
  },
);

const createSocialNetworkItem = createAsyncThunk<
  SocialNetwork,
  CreateUpdateDraftItemPayload,
  { rejectValue: ApiErrorPayload }
>("cms-social-networks/create", async (data, { rejectWithValue }) => {
  try {
    const formData = createDataForUpdate(data);

    const result = await instance.post(
      apiRoutes.socialNetworksCreate,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    return result.data;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

const updateSocialNetworkItem = createAsyncThunk<
  SocialNetwork,
  CreateUpdateDraftItemPayload,
  { rejectValue: ApiErrorPayload }
>("cms-social-networks/update", async (data, { rejectWithValue }) => {
  try {
    const formData = createDataForUpdate(data);

    const result = await instance.put(
      `${apiRoutes.socialNetworksUpdate}/${data.id}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    return result.data;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

const deleteBannerItem = createAsyncThunk(
  "cms-social-networks/delete-banner",
  async ({
    socialNetworkId,
    bannerId,
  }: {
    socialNetworkId: number;
    bannerId: number;
  }) => {
    const result = await instance.delete(
      `${apiRoutes.socialNetworksDeleteBanner}/${socialNetworkId}`,
      {
        data: { mediaId: bannerId },
      },
    );

    return result.data;
  },
);

const bunchUpdateSocialNetworks = createAsyncThunk(
  "cms-social-networks/bunch-update",
  async (data: BunchUpdatePayload) => {
    const result = await instance.put(
      apiRoutes.socialNetworksBunchUpdate,
      data,
    );

    return result.data;
  },
);

const socialNetworksSlice = createSlice({
  name: "cms-social-networks",
  initialState,
  reducers: {
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems =
        state.selectedItems.length !== state.list.length
          ? state.list.map((item) => item.id)
          : [];
    },
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleAddBannerToDraftItem: (state) => {
      const newIndex =
        (state.draftItem.items.length
          ? Math.max(...state.draftItem.items.map((item) => item.id))
          : 0) + 1;

      state.draftItem.items = [
        ...state.draftItem.items,
        {
          id: newIndex,
          href: "",
          priority: newIndex,
          alt: "",
          imageHref: "",
          isModified: false,
        },
      ];
    },

    handleDeleteBannerFromDraftItem: (state, action) => {
      state.draftItem.items = state.draftItem.items.filter(
        (item) => item.id !== action.payload,
      );
      state.draftItem.isModified = true;
    },

    handleUpdateDraftItemBanner: (state, action) => {
      const bannerId = action.payload.id ?? null;

      if (
        bannerId === null ||
        !state.draftItem.items.find((item) => item.id === bannerId)
      ) {
        return;
      }

      const updatedBanners = state.draftItem.items.map((item) => {
        if (item.id === bannerId) {
          return {
            ...item,
            ...action.payload,
            isModified: true,
          };
        }

        return item;
      });

      state.draftItem = {
        ...state.draftItem,
        items: updatedBanners,
      };
    },
    handleClearDraftItem: (state) => {
      state.draftItem = { ...initialSocialNetwork };
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (
      state,
      action: PayloadAction<Partial<SocialNetworkDraftItem>>,
    ) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        isModified: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSocialNetworksList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 1;
    });

    builder.addCase(getSocialNetworkItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
    });

    builder.addCase(deleteSocialNetworkItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });

    builder.addCase(deleteSocialNetworkItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });

    builder.addCase(deleteSocialNetworkItem.rejected, (state) => {
      state.processingItemId = undefined;
    });

    builder.addCase(updateSocialNetworkItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });

    builder.addCase(updateSocialNetworkItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
    });

    builder.addCase(updateSocialNetworkItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(createSocialNetworkItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(createSocialNetworkItem.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });

    builder.addCase(createSocialNetworkItem.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(deleteBannerItem.pending, (state, action) => {
      state.processingBannerId = action.meta.arg.bannerId;
    });

    builder.addCase(deleteBannerItem.fulfilled, (state, action) => {
      state.processingBannerId = undefined;
      state.draftItem = createDraftItem(action.payload);
    });

    builder.addCase(deleteBannerItem.rejected, (state) => {
      state.processingBannerId = undefined;
    });

    builder.addCase(bunchUpdateSocialNetworks.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

export const {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem,
  handleUpdateDraftItemBanner,
  handleAddBannerToDraftItem,
  handleDeleteBannerFromDraftItem,
} = socialNetworksSlice.actions;

export {
  socialNetworksSlice,
  getSocialNetworksList,
  getSocialNetworkItem,
  createSocialNetworkItem,
  updateSocialNetworkItem,
  deleteSocialNetworkItem,
  deleteBannerItem,
  bunchUpdateSocialNetworks,
};
